import React, { useState } from "react";
import TopBarHeader from "../components/TopBarHeader";
import Modal from "../components/Modal";
import * as XLSX from "xlsx";

function Afp() {
  const [cargando, setCargando] = useState(false);
  const [errorMensaje, setErrorMensaje] = useState("");
  const [ruts, setRuts] = useState("");
  const [resultados, setResultados] = useState([]);
  const [contador, setContador] = useState({ procesados: 0, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const mostrarImagen = (screenshot) => {
    setSelectedImage(screenshot);
    setModalVisible(true);
  };

  const descargarExcel = () => {
    // Preparar los datos limpiando los RUTs
    const datosLimpios = resultados.map(({ rut, afp }) => ({
      rut: rut.replace(/\./g, ""), // Limpia los puntos del RUT
      afp,
    }));

    const ws = XLSX.utils.json_to_sheet(datosLimpios);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Resultados");
    XLSX.writeFile(wb, "resultados_afp.xlsx");
  };

  const isValidRut = (rut) => {
    // Eliminar todos los caracteres que no sean números y la letra 'K'
    rut = rut.replace(/[^0-9kK]/g, "");

    // Convertir a mayúscula la letra 'K'
    rut = rut.toUpperCase();

    // Separar el cuerpo del dígito verificador
    const rutBody = rut.slice(0, -1);
    const rutDigit = rut.slice(-1);

    // Calcular el dígito verificador
    let sum = 0;
    let mult = 2;
    for (let i = rutBody.length - 1; i >= 0; i--) {
      sum += parseInt(rutBody.charAt(i)) * mult;
      mult = (mult + 1) % 8 || 2;
    }
    const calculatedDigit = (11 - (sum % 11)) % 11;
    const expectedDigit =
      calculatedDigit === 10 ? "K" : calculatedDigit.toString();

    // Comparar el dígito verificador calculado con el ingresado
    return rutDigit === expectedDigit;
  };

  const validateRuts = () => {
    const allRuts = ruts.split(/\n|\s+/).filter(Boolean);
    const invalidRuts = allRuts.filter((rut) => rut && !isValidRut(rut));

    if (invalidRuts.length > 0) {
      const invalidRutsMessage = `Los siguientes RUT son inválidos: ${invalidRuts.join(
        ", "
      )}`;
      setErrorMensaje(invalidRutsMessage);
      setTimeout(() => setErrorMensaje(""), 5000);
    } else {
      obtenerAfpPorRuts();
    }
  };

  const handleTextAreaChange = (event) => {
    const maxLines = 50;
    const allowedChars = /^[0-9Kk\-\.\n]+$/;
    const inputValue = event.target.value;

    // Si el valor ingresado está vacío, limpia el estado y el mensaje de error
    if (inputValue === "") {
      setRuts("");
      setErrorMensaje("");
      return;
    }

    // Verifica si el valor ingresado contiene solo caracteres permitidos
    if (allowedChars.test(inputValue)) {
      const inputLines = inputValue.split("\\n");
      if (inputLines.length > maxLines) {
        // Establece el mensaje de error
        setErrorMensaje(`No puedes ingresar más de ${maxLines} líneas.`);
        // Limpia el mensaje de error después de 5 segundos
        setTimeout(() => setErrorMensaje(""), 5000);
      } else {
        // Actualiza el estado con el valor ingresado si está dentro del límite de líneas
        setRuts(inputValue);
        // Asegúrate de borrar cualquier mensaje de error existente si el usuario corrige el error
        if (errorMensaje) setErrorMensaje("");
      }
    } else {
      // Si el valor ingresado contiene caracteres no permitidos, no actualiza el estado
      setErrorMensaje();
      setTimeout(() => setErrorMensaje(""), 5000);
    }
  };

  const formatearRut = (rutSinFormato) => {
    // Elimina cualquier carácter no numérico excepto 'K' y 'k'
    const rutLimpio = rutSinFormato.replace(/[^0-9kK]/g, "");

    if (rutLimpio.length <= 1) return rutLimpio; // Retorna el valor tal cual si es demasiado corto para formatear

    // Separa el cuerpo del RUT del dígito verificador
    let digitoVerificador = rutLimpio.slice(-1).toUpperCase(); // Asegura que 'K' esté en mayúsculas
    let cuerpoRut = rutLimpio.slice(0, -1);

    // Elimina el cero inicial en el cuerpo del RUT
    cuerpoRut = cuerpoRut.replace(/^0+/, "");

    // Formatea la parte numérica del RUT
    cuerpoRut = cuerpoRut.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return `${cuerpoRut}-${digitoVerificador}`;
  };

  // Modificamos la función para procesar múltiples RUTs
  const obtenerAfpPorRuts = async () => {
    const listaRuts = ruts.split("\n").filter(Boolean);
    const resultadosTemporales = [];
    setContador({ procesados: 0, total: listaRuts.length });
    setCargando(true);

    for (let [index, rut] of listaRuts.entries()) {
      const resultado = await obtenerAfpPorRut(rut);
      resultado.rut = formatearRut(resultado.rut);
      resultadosTemporales.push(resultado);
      setContador({ procesados: index + 1, total: listaRuts.length });
    }

    setResultados(resultadosTemporales);
    setCargando(false);
  };

  // Modificamos para que devuelva un objeto en vez de actualizar el estado directamente
  const obtenerAfpPorRut = async (rut) => {
    try {
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_STAGE}/scraper/spensiones-afp`;
      const rutLimpio = rut.replace(/\./g, "");
      const payload = { rut: rutLimpio };

      let response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (
        !data ||
        data.length === 0 ||
        Object.keys(data).length === 0 ||
        data.afp === null
      ) {
        return { rut, afp: "Sin AFP", screenshot: null };
      }
      return { rut, afp: data.afp, screenshot: data.screenshot };
    } catch (error) {
      console.error("Error al obtener información de la AFP por RUT:", error);
      return { rut, afp: "Error al obtener la AFP", screenshot: null };
    }
  };

  return (
    <div>
      <TopBarHeader />
      <h1 className="h1-titulo-2">AFP</h1>
      <div className="upload-container">
        <textarea
          placeholder="Ingresa los RUTs separados por un salto de línea"
          className="textarea-moderno"
          value={ruts}
          onChange={handleTextAreaChange}
          rows="10"
        />

        {ruts && (
          <>
            <button className="button-submit" onClick={validateRuts}>
              Obtener AFPs
            </button>
          </>
        )}
        {!cargando && resultados.length > 0 && (
          <button className="button" onClick={descargarExcel}>
            Descargar a Excel
          </button>
        )}
        {errorMensaje && <div className="error-mensaje">{errorMensaje}</div>}
      </div>

      {cargando && (
        <div className="loader-container">
          <div className="loader-content">
            <div className="loader"></div>
            <div className="archivos-contador">
              RUTs procesados: {contador.procesados}/{contador.total}
            </div>
          </div>
        </div>
      )}
      {!cargando && resultados.length > 0 && (
        <div className="resultado-afp">
          <table>
            <thead>
              <tr>
                <th>RUT</th>
                <th>AFP</th>
                <th>Imagen</th>
              </tr>
            </thead>
            <tbody>
              {resultados.map(({ rut, afp, screenshot }) => (
                <tr key={rut}>
                  <td>{rut}</td>
                  <td>{afp}</td>
                  {afp && afp !== "Sin AFP" && (
                    <td>
                      <button
                        className="button-table"
                        onClick={() => mostrarImagen(screenshot)}
                      >
                        Ver AFP
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalVisible && (
        <Modal isOpen={modalVisible} onClose={() => setModalVisible(false)}>
          <img
            src={`data:image/png;base64,${selectedImage}`}
            alt="Screenshot"
          />
        </Modal>
      )}
    </div>
  );
}

export default Afp;
